<template>
  <main class="login-page">
    <header class="login-header">
      <button class="entry-back-button" type="button" @click="returnToEntry">
        <el-icon><ArrowLeft /></el-icon>
        返回上一层
      </button>
      <div class="welcome">
        <p>你好，欢迎使用</p>
        <h1>云手机</h1>
      </div>
      <div class="auth-tabs" role="tablist" aria-label="账号入口">
        <button id="login-tab" role="tab" :aria-selected="!isRegistering" aria-controls="auth-form" :disabled="submitting" @click="switchMode(false)">账号登录</button>
        <button id="register-tab" role="tab" :aria-selected="isRegistering" aria-controls="auth-form" :disabled="submitting" @click="switchMode(true)">注册账号</button>
      </div>
    </header>

    <section class="form-region">
      <form id="auth-form" class="auth-form" role="tabpanel" :aria-labelledby="isRegistering ? 'register-tab' : 'login-tab'" novalidate @submit.prevent="submit">
        <div class="field-group">
          <label for="account">账号</label>
          <div class="field" :class="{ invalid: accountError }">
            <el-icon><User /></el-icon>
            <input id="account" v-model="form.username" name="username" autocomplete="username" autocapitalize="none" :spellcheck="false" :disabled="submitting" :maxlength="isRegistering ? 12 : undefined" :placeholder="isRegistering ? '设置账号' : '请输入账号或手机号'" :aria-invalid="Boolean(accountError)" aria-describedby="account-hint" @blur="touched.username = true">
          </div>
          <p id="account-hint" class="field-hint" :class="{ error: accountError }">{{ accountError || (isRegistering ? '6–12 位字母或数字，首位为字母' : '使用已有账号或手机号登录') }}</p>
        </div>

        <div class="field-group">
          <label for="password">密码</label>
          <div class="field" :class="{ invalid: passwordError }">
            <el-icon><Lock /></el-icon>
            <input id="password" ref="passwordInput" v-model="form.password" name="password" :type="passwordVisible ? 'text' : 'password'" :autocomplete="isRegistering ? 'new-password' : 'current-password'" :disabled="submitting" :maxlength="isRegistering ? 12 : undefined" :placeholder="isRegistering ? '设置密码' : '请输入密码'" :aria-invalid="Boolean(passwordError)" aria-describedby="password-hint" @blur="touched.password = true">
            <button type="button" class="visibility-button" :aria-label="passwordVisible ? '隐藏密码' : '显示密码'" :title="passwordVisible ? '隐藏密码' : '显示密码'" :aria-pressed="passwordVisible" @click="passwordVisible = !passwordVisible"><el-icon><component :is="passwordVisible ? Hide : View" /></el-icon></button>
          </div>
          <p id="password-hint" class="field-hint" :class="{ error: passwordError }">{{ passwordError || (isRegistering ? '6–12 位字母或数字' : '\u00a0') }}</p>
        </div>

        <div v-if="isRegistering" class="field-group">
          <label for="confirm-password">确认密码</label>
          <div class="field" :class="{ invalid: confirmError }">
            <el-icon><Lock /></el-icon>
            <input id="confirm-password" v-model="form.confirmPassword" name="confirmPassword" type="password" autocomplete="new-password" :disabled="submitting" maxlength="12" placeholder="再次输入密码" :aria-invalid="Boolean(confirmError)" aria-describedby="confirm-hint" @blur="touched.confirmPassword = true">
          </div>
          <p id="confirm-hint" class="field-hint error">{{ confirmError || '\u00a0' }}</p>
        </div>

        <div class="agreement">
          <input id="agreed" v-model="agreed" type="checkbox" :disabled="submitting">
          <div><label for="agreed">我已阅读并同意</label><br><button type="button" class="legal-link" @click="legalDocument = 'agreement'">《用户服务协议》</button><span>与</span><button type="button" class="legal-link" @click="legalDocument = 'privacy'">《隐私政策》</button></div>
        </div>

        <el-button class="submit-button" type="primary" native-type="submit" :loading="submitting" :disabled="!canSubmit">
          {{ submitting ? (isRegistering ? '正在注册' : '登录中') : (isRegistering ? '注册' : '登录') }}
        </el-button>
        <p v-if="message" class="form-message" :class="{ success: successMessage }" role="status">{{ message }}</p>
      </form>
    </section>

    <el-dialog :model-value="Boolean(legalDocument)" :title="legalDocument === 'privacy' ? '隐私政策' : '用户服务协议'" width="640px" class="legal-dialog" @close="legalDocument = ''">
      <LegalDocumentContent v-if="legalDocument" :document="legalDocument" />
      <template #footer><el-button type="primary" @click="legalDocument = ''">关闭</el-button></template>
    </el-dialog>
  </main>
</template>

<script setup>
import { computed, nextTick, reactive, ref } from 'vue'
import { ArrowLeft, Hide, Lock, User, View } from '@element-plus/icons-vue'
import { useRoute, useRouter } from 'vue-router'
import { loginByPassword, registerAccount } from '../api/auth'
import { getRequestErrorMessage } from '../api/http'
import { saveSession } from '../utils/authSession'
import { clearAuthFailure, readAuthFailureMessage } from '../utils/authDiagnostics'
import { clearWebEntryPreference } from '../utils/entryPreference'
import LegalDocumentContent from '../components/LegalDocumentContent.vue'

const router = useRouter()
const route = useRoute()
const isRegistering = ref(false)
const submitting = ref(false)
const agreed = ref(false)
const passwordVisible = ref(false)
const message = ref(readAuthFailureMessage())
const successMessage = ref(false)
const legalDocument = ref('')
const passwordInput = ref(null)
const form = reactive({ username: '', password: '', confirmPassword: '' })
const touched = reactive({ username: false, password: false, confirmPassword: false })

// Registration rules must not prevent existing accounts from signing in.
const accountValid = computed(() => isRegistering.value
  ? /^[A-Za-z][A-Za-z\d]{5,11}$/.test(form.username) : Boolean(form.username.trim()))
const passwordValid = computed(() => isRegistering.value
  ? /^[A-Za-z\d]{6,12}$/.test(form.password) : Boolean(form.password))
const passwordsMatch = computed(() => form.password === form.confirmPassword && Boolean(form.password))
const accountError = computed(() => touched.username && !accountValid.value ? (isRegistering.value ? '账号为 6–12 位字母数字，首位为字母' : '请输入账号或手机号') : '')
const passwordError = computed(() => touched.password && !passwordValid.value ? (isRegistering.value ? '密码为 6–12 位字母或数字' : '请输入密码') : '')
const confirmError = computed(() => touched.confirmPassword && !passwordsMatch.value ? '两次输入的密码不一致' : '')
const canSubmit = computed(() => agreed.value && accountValid.value && passwordValid.value && (!isRegistering.value || passwordsMatch.value))

function switchMode(registering) {
  isRegistering.value = registering
  form.confirmPassword = ''
  passwordVisible.value = false
  message.value = ''
  successMessage.value = false
  Object.keys(touched).forEach(key => { touched[key] = false })
}

function returnToEntry() {
  clearWebEntryPreference()
  router.replace('/')
}

async function submit() {
  if (submitting.value || !canSubmit.value) return
  clearAuthFailure()
  submitting.value = true
  message.value = ''
  successMessage.value = false
  const credentials = { username: form.username, password: form.password }

  try {
    if (isRegistering.value) {
      await registerAccount(credentials)
      switchMode(false)
      form.password = ''
      successMessage.value = true
      message.value = '注册成功，请使用新账号登录。'
    } else {
      saveSession(await loginByPassword(credentials))
      const redirect = route.query.redirect
      // Accept only known in-app destinations from the return URL.
      const destination = typeof redirect === 'string' && /^\/(devices(?:\/\d+)?|profile)(?:[?#]|$)/.test(redirect)
        ? redirect : '/devices'
      await router.replace(destination)
    }
  } catch (error) {
    message.value = getRequestErrorMessage(error, isRegistering.value ? '注册失败' : '登录失败')
  } finally {
    submitting.value = false
    if (successMessage.value) {
      await nextTick()
      passwordInput.value?.focus()
    }
  }
}
</script>

<style scoped>
.login-page { min-height: 100vh; min-height: 100svh; background: #fff; }
.login-header { position: relative; min-height: 300px; padding: calc(42px + env(safe-area-inset-top, 0px)) 28px 0; color: white; background: #047f84 url('../assets/login-cloud-phone.webp') center 69% / cover no-repeat; isolation: isolate; }
.login-header::before { position: absolute; z-index: -1; inset: 0; content: ''; background: rgba(0, 90, 96, .46); }
.entry-back-button { position: absolute; top: calc(18px + env(safe-area-inset-top, 0px)); right: 20px; z-index: 1; display: inline-flex; align-items: center; gap: 4px; min-height: 36px; padding: 0 10px; border: 0; border-radius: 8px; color: white; background: rgba(0, 0, 0, .16); cursor: pointer; font: inherit; font-size: 13px; }
.entry-back-button:hover { background: rgba(0, 0, 0, .26); }
.entry-back-button:focus-visible { outline: 2px solid white; outline-offset: 2px; }
.welcome { max-width: 432px; margin: 0 auto; }
.welcome p { margin: 0 0 10px; font-size: 20px; }
.welcome h1 { margin: 0; font-size: 38px; font-weight: 650; }
.auth-tabs { display: flex; max-width: 432px; margin: 56px auto 0; gap: 32px; }
.auth-tabs button { position: relative; flex: 1; min-height: 62px; padding: 0 12px 18px; color: rgba(255, 255, 255, .78); background: transparent; border: 0; font-size: 17px; cursor: pointer; }
.auth-tabs button[aria-selected='true'] { color: #fff; font-weight: 650; }
.auth-tabs button[aria-selected='true']::after { position: absolute; bottom: 12px; left: calc(50% - 14px); width: 28px; height: 3px; background: white; border-radius: 2px; content: ''; }
.form-region { padding: 32px 28px calc(40px + env(safe-area-inset-bottom, 0px)); background: #fff; }
.auth-form { max-width: 432px; margin: 0 auto; }
.field-group { margin-bottom: 17px; }
.field-group > label { display: block; margin: 0 0 9px; color: #364846; font-size: 14px; font-weight: 500; }
.field { display: flex; align-items: center; gap: 12px; min-height: 54px; padding: 0 14px; background: #f1f6f5; border: 1px solid transparent; border-radius: 14px; }
.field:focus-within { border-color: var(--accent); }
.field.invalid { border-color: #d74856; }
.field > .el-icon { color: #74918c; font-size: 19px; }
.field input { min-width: 0; width: 100%; height: 52px; padding: 0; color: #1d3130; font-size: 16px; background: transparent; border: 0; outline: 0; }
.field input::placeholder { color: #929f9d; }
.field-hint { min-height: 17px; margin: 7px 0 0 2px; font-size: 12px; line-height: 1.5; color: #8a9597; }
.error, .form-message { color: #c54050; }
.visibility-button { flex: 0 0 32px; width: 32px; height: 40px; display: grid; place-items: center; padding: 0; border: 0; color: #718783; background: transparent; cursor: pointer; font-size: 19px; }
.agreement { display: flex; align-items: center; gap: 10px; margin: 22px 0 24px; color: #607574; font-size: 13px; line-height: 1.8; }
.agreement input { flex-shrink: 0; width: 18px; height: 18px; margin: 0; accent-color: var(--accent); cursor: pointer; }
.agreement label { cursor: pointer; }
.legal-link { padding: 0; color: var(--accent); font-size: 13px; background: none; border: none; cursor: pointer; }
.submit-button { width: 100%; height: 54px; border-radius: 16px; font-weight: 600; font-size: 17px; }
.submit-button.is-disabled { background: #e0ecea; border-color: #e0ecea; color: #809894; }
.form-message { margin: 18px 0 0; font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; }
.form-message.success { color: var(--accent); }
.login-page :deep(.legal-dialog .el-dialog__body) { max-height: 60svh; overflow-y: auto; }
@media (min-width: 760px) { .login-header { background-size: 1024px auto; background-position: center 69%; } }
@media (max-width: 374px) { .login-header { padding-right: 20px; padding-left: 20px; } .form-region { padding-right: 20px; padding-left: 20px; } .agreement, .legal-link { font-size: 12px; } }
@media (max-height: 650px) { .login-header { min-height: 240px; padding-top: calc(28px + env(safe-area-inset-top, 0px)); } .auth-tabs { margin-top: 25px; } .form-region { padding-top: 22px; } }
</style>
